import { FormikProvider, useFormik } from 'formik';
import { useMemo } from 'react';

import Alert, { AlertSeverity } from '@perpay-web/components/base/Alert/Alert';
import CustomButton from '@perpay-web/components/base/CustomButton/CustomButton';
import NonFieldErrors from '@perpay-web/components/base/NonFieldErrors/NonFieldErrors';
import FormikPasswordInput from '@perpay-web/components/base/PasswordInput/FormikPasswordInput';
import FormikTextInput from '@perpay-web/components/base/TextInput/FormikTextInput';
import FocusContent from '@perpay-web/components/composite/FocusLayout/FocusContent';
import FocusFooter from '@perpay-web/components/composite/FocusLayout/FocusFooter';
import FocusLayout from '@perpay-web/components/composite/FocusLayout/FocusLayout';
import Stack from '@perpay-web/components/design-system/Stack/Stack';
import Captcha from '@perpay-web/fintech/components/base/Captcha/Captcha';
import CustomLink from '@perpay-web/fintech/components/base/CustomLink/CustomLink';
import ResponsiveHeader from '@perpay-web/fintech/components/base/ResponsiveHeader/ResponsiveHeader';
import LoginWithBiometricsContainer from '@perpay-web/fintech/components/composite/LoginWithBiometrics/LoginWithBiometricsContainer';
import { paths } from '@perpay-web/fintech/props/appPaths';
import { loginFormSchema } from '@perpay-web/fintech/validators/formBased';
import { useIsMobile } from '@perpay-web/hooks/useIsMobile';
import { EndToEndTestIdProvider } from '@perpay-web/utils/endToEndTestIds';
import { validateForm } from '@perpay-web/validators/formBased';

import LoginBanner from './LoginBanner';
import styles from './LoginForm.scss';

const LoginForm = ({
    onSubmit,
    errors = {
        email: [],
        password: [],
        message: [],
    },
    isLoading = false,
    mfa = {},
    initialValues = {
        email_or_phone: '',
        password: '',
    },
    isExistingUser = false,
    showWafCaptcha = false,
}) => {
    const { isMobile } = useIsMobile();

    const formik = useFormik({
        initialValues,
        onSubmit: (values) => {
            const isEmail = values.email_or_phone.includes('@');

            const emailOrPhone =
                isEmail || values.email_or_phone.startsWith('+1')
                    ? values.email_or_phone
                    : `+1${values.email_or_phone}`;

            const faceIdInput = document.getElementById('face_id');

            const newValues = {
                ...values,
                email_or_phone: emailOrPhone,
                face_id: faceIdInput ? faceIdInput.checked : null,
            };
            onSubmit(newValues);
        },
        validate: (values) => validateForm(values, loginFormSchema),
        validateOnChange: false,
    });

    const mfaAlert = useMemo(
        () =>
            mfa && mfa.error && mfa.error.throttle ? (
                <Alert
                    content={mfa.error.throttle.message[0]}
                    severity={AlertSeverity.DANGER}
                    className={styles['throttle-alert']}
                />
            ) : null,
        [mfa],
    );

    const onCaptchaSuccessCallback = () => {
        formik.submitForm();
    };

    return (
        <EndToEndTestIdProvider testId='login-form'>
            {!isMobile && mfaAlert}
            <FormikProvider value={formik}>
                <form onSubmit={formik.handleSubmit}>
                    <FocusLayout className={styles['focus-layout']}>
                        <FocusContent>
                            {isMobile ? mfaAlert : null}
                            {isMobile && isExistingUser ? (
                                <LoginBanner />
                            ) : null}
                            <ResponsiveHeader className={styles.title}>
                                Login to Perpay
                            </ResponsiveHeader>
                            <Stack className={styles['inner-stack']}>
                                <EndToEndTestIdProvider testId='login-email-input'>
                                    <FormikTextInput
                                        label='Email address or phone number'
                                        placeholder='me@example.com or 5551234567'
                                        name='email_or_phone'
                                        type='text'
                                        inputMode='email'
                                        errors={errors}
                                    />
                                </EndToEndTestIdProvider>
                                <EndToEndTestIdProvider testId='login-password-input'>
                                    <FormikPasswordInput
                                        name='password'
                                        errors={errors}
                                    />
                                </EndToEndTestIdProvider>
                                <CustomLink href={paths.resetPassword.path}>
                                    Forgot password?
                                </CustomLink>
                                <NonFieldErrors errors={errors} />
                                <Captcha
                                    showWafCaptcha={showWafCaptcha}
                                    onSuccessCallback={onCaptchaSuccessCallback}
                                />
                            </Stack>
                            <LoginWithBiometricsContainer
                                className={styles.biometrics}
                                shouldPromptLogin
                            />
                        </FocusContent>
                        <FocusFooter>
                            <EndToEndTestIdProvider testId='login-button'>
                                <CustomButton
                                    action='primary'
                                    loading={isLoading}
                                    type='submit'
                                >
                                    Login
                                </CustomButton>
                            </EndToEndTestIdProvider>
                            <p className={styles.signup}>
                                No account yet?{' '}
                                <EndToEndTestIdProvider testId='sign-up-button'>
                                    <CustomLink href={paths.signUp.path}>
                                        Sign up
                                    </CustomLink>
                                </EndToEndTestIdProvider>
                            </p>
                        </FocusFooter>
                    </FocusLayout>
                </form>
            </FormikProvider>
        </EndToEndTestIdProvider>
    );
};

export default LoginForm;
